/**
 * Recording-link lifetime — the `recording_link_ttl` bot-config value.
 *
 * Stored as "<n>h" or "<n>d", n a positive integer ("2d", "36h"). The unit is
 * kept as entered, so "24h" round-trips as 24 hours rather than turning into
 * "1 day". The backend (recording_links.go: parseRecordingLinkTTL) accepts 1
 * hour to 30 days and answers anything else with a 400, so the editor clamps to
 * the same bounds before it writes. Absent, unparseable or out of range reads
 * as the backend default, 2 days — what the server would actually use.
 */
export type LinkTTLUnit = 'h' | 'd';

export interface LinkTTL {
  n: number;
  unit: LinkTTLUnit;
}

export const DEFAULT_LINK_TTL: Readonly<LinkTTL> = { n: 2, unit: 'd' };

/** Largest n per unit. Both are 30 days, the backend's ceiling. */
export const LINK_TTL_MAX: Readonly<Record<LinkTTLUnit, number>> = { h: 720, d: 30 };

/** "36h" → { n: 36, unit: 'h' }; empty/invalid/out of range → 2 days. */
export function parseLinkTTL(raw: string | null | undefined): LinkTTL {
  const m = /^([1-9][0-9]*)([hd])$/.exec((raw ?? '').trim().toLowerCase());
  if (!m) return { ...DEFAULT_LINK_TTL };
  const unit = m[2] as LinkTTLUnit;
  const n = Number(m[1]);
  if (n > LINK_TTL_MAX[unit]) return { ...DEFAULT_LINK_TTL };
  return { n, unit };
}

/** Whole number within the backend's bounds for `unit` (1..720 h, 1..30 d). */
export function clampLinkTTL(n: number, unit: LinkTTLUnit): number {
  if (!Number.isFinite(n)) return 1;
  return Math.min(LINK_TTL_MAX[unit], Math.max(1, Math.floor(n)));
}

/** { n: 36, unit: 'h' } → "36h", clamped so the PUT never 400s. */
export function formatLinkTTL({ n, unit }: LinkTTL): string {
  return `${clampLinkTTL(n, unit)}${unit}`;
}

/**
 * Would the backend accept `raw` as stored? Absent/empty counts as valid —
 * the server reads it as its 2-day default.
 */
export function isValidLinkTTL(raw: string | null | undefined): boolean {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === '') return true;
  const m = /^([1-9][0-9]*)([hd])$/.exec(s);
  return Boolean(m) && Number(m![1]) <= LINK_TTL_MAX[m![2] as LinkTTLUnit];
}

/**
 * A loosely written lifetime ("36h", "2 days", "1 week", "60d") → the stored
 * form, clamped to the backend's bounds ("60d" → "30d"); undefined when it
 * can't be read. The Sidekick writes this field as free text, and a value the
 * backend rejects would fail the whole save while the editor showed 2 days.
 */
export function coerceLinkTTL(text: string): string | undefined {
  const m = /^([1-9][0-9]*)\s*(h|hrs?|hours?|d|days?|w|wks?|weeks?)$/.exec(text.trim().toLowerCase());
  if (!m) return undefined;
  const n = Number(m[1]);
  const u = m[2][0];
  if (u === 'w') return formatLinkTTL({ n: n * 7, unit: 'd' });
  return formatLinkTTL({ n, unit: u as LinkTTLUnit });
}

/** { n: 2, unit: 'd' } → "2 days"; { n: 1, unit: 'h' } → "1 hour". */
export function describeLinkTTL({ n, unit }: LinkTTL): string {
  const word = unit === 'h' ? 'hour' : 'day';
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}
